<script>
  import { onMount } from "svelte";
  import { writable } from "svelte/store";
  import { fetchWithSession } from "../../../../lib/util/fetchWithSession";
  import { activitiesStoreProject } from "@stores/projectStores.js";
  import { fly } from "svelte/transition";
  import { Button } from "paxray-components-svelte"

  import { useSvelteFlow } from "@xyflow/svelte";
  import { Loading, ToastNotification } from "carbon-components-svelte";
  import FlowPlotter from "../../analysis-frontend/analysis/components/FlowPlotterTasks.svelte";
  import { FIT_SCREEN_ICON, FULLSCREEN_ICON, FULLSCREEN_OFF_ICON } from '@utils/icons.js';

  const urlParams = new URLSearchParams(window.location.search);
  const idproject = urlParams.get("idproject");
  export let currentTask = "";
  export let fullscreen = false;
  export let controller = new AbortController();

  // temp data
  let selectedClusters = [];
  let hadSelectedClusters = false;
  let casesSelected = false;
  let seeMinimap = false;
  let loading = true;
  let loadingCases = true;
  let paxbotVisible = false;
  let paxbotMessage = "";
  let paxbotTitle = "";
  let selectedProcesses = [];

  const { fitView, zoomIn, zoomOut, getZoom } = useSvelteFlow();
  let zoomLevel = writable((getZoom() * 100).toFixed(0));
  let initialZoomValue = writable(0);

  let selectedNode = "";
  let selectedNodes = [];
  let expandedNodes = [];
  let fullGraphView;
  let fullNodeInfo = true;
  let triggerUpdate = false;
  let nvariants = 1;
  let minFrequency = 0;
  let graph = {
    activityMappingCluster: [],
    allactivitymapping: [],
    edges: [],
    executionFrequency: 0,
    nactivities: 0,
    nodes: [],
    nusers: 0,
    nvariants: 0,
    timepercase: 0,
    totaltime: 0,
    activityKpis: {},
    uniquePrograms: [],
    uniqueActivities: 0,
    variants: [],
  };
  let casesGraph = {
    nodes: [],
    edges: [],
  };
  export let graphActivities = [];

  $: ({ allActivities, toastMsg, saveSuccessful } = $activitiesStoreProject);

  let activitiesToShow = [];

  let flowPlotterElement;

  let taskList = [];
  let subprocesses = [];
  let processOptions = [];

  let subprocessOptions = [];
  let lastCurrentTask = "";
  let baseActivityNodes = [];

  $: if (currentTask && currentTask !== lastCurrentTask) {
    lastCurrentTask = currentTask;
    expandedNodes = [];
    baseActivityNodes = [];
  }

  $: if (currentTask && allActivities) getTaskGraph(currentTask);

  $: if (triggerUpdate) getTaskGraph(currentTask);

  $: {
    selectedClusters = selectedProcesses.map((process) => process.id);
  }

  $: {
    if (fullGraphView === 3) {
      casesSelected = true;
    } else {
      casesSelected = false;
    }
  }

  $: nodesData = graph["nodes"];

  $: {
    if (selectedClusters.length) {
      hadSelectedClusters = true;
    } else if (hadSelectedClusters) {
      resetGraph();
      hadSelectedClusters = false;
    }
  }

  async function getTaskGraph(taskid) {
    if (toastMsg === null) return;
    loading = true;
    controller = new AbortController();
    try {
      // The node lists go in the body: expanding all activities made the URL too long (414/431).
      const response = await fetchWithSession(
        `/api/damprojects/getProcessGraph?project_id=${idproject}&recordingid=${taskid}`,
        {
          method: "POST",
          body: JSON.stringify({ expandedNodes, baseNodes: baseActivityNodes }),
          suppressSuccessToast: true,
          signal: controller.signal,
        },
      );
      const data = await response.json();
      graph = data;
      if (!expandedNodes.length) {
        baseActivityNodes = data.baseNodes || [];
      }
      loading = false;
      triggerUpdate = false;
      graphActivities =
        data.activities.map((activity) => ({
          ...activity,
          activity_name: activity.activityname
        })) ;
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      }
    }
  }

  //$:console.log({ graphActivities });

  function toggleFullscreen() {
    fullscreen = !fullscreen;
  }

  async function incrementZoom() {
    await zoomIn();
    updateZoomLevel();
  }

  async function decrementZoom() {
    await zoomOut();
    updateZoomLevel();
  }

  async function updateZoomLevel() {
    zoomLevel.set((getZoom() * 100).toFixed(0));
  }

  function expandAllActivities() {
    const nodesToExpand = graph["nodes"]
      .map((node) => {
        if (node.isSubProcess) return node.id;
      })
      .filter((node) => node !== undefined);
    expandedNodes = [...expandedNodes, ...nodesToExpand];
    getTaskGraph(currentTask);
  }

  function collapseAllActivities() {
    expandedNodes = [];
    getTaskGraph(currentTask);
  }

  function resetGraph() {
    graph = {
      activityMappingCluster: [],
      allactivitymapping: [],
      edges: [],
      executionFrequency: 0,
      nactivities: 0,
      nodes: [],
      nusers: 0,
      nvariants: 0,
      timepercase: 0,
      totaltime: 0,
      activityKpis: {},
      uniquePrograms: [],
      uniqueActivities: 0,
      variants: [],
    };
    casesGraph = {
      nodes: [],
      edges: [],
    };
  }

  onMount(() => {
    if (toastMsg) toastMsg = "";
  });
</script>

<div class="main_wrapper">
  <div class="canvas">
    {#if casesSelected}
      {#if loadingCases}
        <div class="loading_wrapper">
          <Loading withOverlay={false} />
        </div>
      {/if}
    {:else if !currentTask}
      <div class="no_process">Select Task</div>
    {:else if loading}
      <div class="loading_wrapper">
        <Loading withOverlay={false} />
      </div>
    {:else}
      {#key `${minFrequency} - ${nvariants}`}
        <FlowPlotter
          bind:this={flowPlotterElement}
          {nodesData}
          edgesData={graph["edges"]}
          variants={graph["variants"]}
          subgraphs={graph["activityMappingCluster"]}
          kpis={graph["activityKpis"] || {}}
          width="100%"
          height="100%"
          bind:selectedNode
          bind:selectedNodes
          bind:expandedNodes
          {fullGraphView}
          {fullNodeInfo}
          bind:zoomLevel={$zoomLevel}
          bind:triggerUpdate
          {nvariants}
          {minFrequency}
          {updateZoomLevel}
          executionFrequency={graph["executionFrequency"]}
          {seeMinimap}
          bind:activitiesToShow
          bind:paxbotVisible
          bind:paxbotMessage
          bind:paxbotTitle
          bind:graphActivities
          previewMode
        />
      {/key}
    {/if}
  </div>
  <div class="menu_overlay bottom">
    <div class="button_wrapper">
      <Button
        text={expandedNodes.length
          ? "Collapse Activities"
          : "Expand Activities"}
        kind="secondary"
        on:click={expandedNodes.length
          ? collapseAllActivities
          : expandAllActivities}
      />
    </div>
    <div class="button_wrapper">
      <Button
        kind="plus-minus"
        text="Zoom"
        bind:value={$zoomLevel}
        showValue={false}
        unit=""
        reset={() => {
          fitView();
          updateZoomLevel();
        }}
        increment={incrementZoom}
        decrement={decrementZoom}
      />
      <Button
        kind="secondary"
        icon={FIT_SCREEN_ICON}
        on:click={() => {
          fitView();
          updateZoomLevel();
        }}
      />
      <Button
        kind="secondary"
        icon={fullscreen ? FULLSCREEN_OFF_ICON : FULLSCREEN_ICON}
        on:click={toggleFullscreen}
      />
    </div>
  </div>
</div>

{#if toastMsg}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:absolute; top:0px; right:50px; z-index:100;"
  >
    <ToastNotification
      fullWidth
      hideAfter={saveSuccessful ? 3000 : null}
      timeout={saveSuccessful ? 3000 : null}
      kind={saveSuccessful ? "success" : "error"}
      title={saveSuccessful ? "Success" : "Error"}
      subtitle={toastMsg}
      caption={new Date().toLocaleString()}
      on:close={(e) => {
        e.preventDefault();
        toastMsg = "";
      }}
    />
  </div>
{/if}

<style>
  .button_wrapper {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .main_wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .menu_overlay {
    position: absolute;
    width: 100%;
    z-index: 1; /* Stellt sicher, dass das Overlay über dem Canvas liegt */
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .bottom {
    bottom: 0;
  }

  .canvas {
    height: 100%;
    min-height: 100%;
    width: 100%;
    z-index: 0; /* Platziert das Canvas hinter den Overlays */
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .no_process {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    font-size: 2rem;
    color: var(--pax-black);
  }
</style>
